/**
 * Utilities for generating CommandBar data attributes
 *
 * @description Helper functions to generate consistent data-commandbar-id attributes
 * across all sections of the application for CommandBar integration
 *
 * @example
 * const titleId = generateCommandBarId('reportes', 'Elige la opción que deseas consultar');
 * // returns "reportes-elige-la-opcion-que-deseas-consultar"
 *
 * const actionId = generateCommandBarId('productos', 'Nuevo producto', 'agregar');
 * // returns "productos-nuevo-producto-agregar"
 */

/**
 * Converts a text string to a valid CommandBar ID format
 *
 * @param {string} text - The text to convert
 * @returns {string} Formatted text for CommandBar ID (lowercase, kebab-case)
 *
 * @example
 * formatTextForCommandBarId('Elige la opción que deseas consultar');
 * // returns "elige-la-opcion-que-deseas-consultar"
 */
export const formatTextForCommandBarId = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  return (
    text
      .toLowerCase()
      .trim()
      // Replace accented characters
      .replace(/[áàäâ]/g, 'a')
      .replace(/[éèëê]/g, 'e')
      .replace(/[íìïî]/g, 'i')
      .replace(/[óòöô]/g, 'o')
      .replace(/[úùüû]/g, 'u')
      .replace(/[ñ]/g, 'n')
      .replace(/[ç]/g, 'c')
      // Remove special characters and replace spaces/multiple chars with hyphens
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s-]+/g, '-')
      // Remove leading/trailing hyphens
      .replace(/^-+|-+$/g, '')
  );
};

/**
 * Generates a CommandBar data-commandbar-id attribute value
 *
 * @param {string} section - The section name (e.g., 'reportes', 'inventarios')
 * @param {string} title - The title text to format
 * @param {string} [action] - Optional action to append (e.g., 'agregar', 'editar')
 * @returns {string} Complete CommandBar ID in format "section-formatted-title" or "section-formatted-title-action"
 *
 * @example
 * generateCommandBarId('reportes', 'Elige la opción que deseas consultar');
 * // returns "reportes-elige-la-opcion-que-deseas-consultar"
 *
 * generateCommandBarId('productos', 'Nuevo producto', 'agregar');
 * // returns "productos-nuevo-producto-agregar"
 */
export const generateCommandBarId = (section: string, title: string, action?: string): string => {
  const formattedSection = formatTextForCommandBarId(section);
  const formattedTitle = formatTextForCommandBarId(title);

  if (!formattedSection || !formattedTitle) {
    return '';
  }

  let commandBarId = `${formattedSection}-${formattedTitle}`;

  if (action) {
    const formattedAction = formatTextForCommandBarId(action);
    if (formattedAction) {
      commandBarId += `-${formattedAction}`;
    }
  }

  return commandBarId;
};

/**
 * Creates the complete data-commandbar-id attribute object for React components
 *
 * @param {string} section - The section name
 * @param {string} title - The title text
 * @param {string} [action] - Optional action to append
 * @returns {object} Object with data-commandbar-id property
 *
 * @example
 * <Typography {...createCommandBarDataAttribute('reportes', 'Ventas por categoría')}>
 *   Ventas por categoría
 * </Typography>
 *
 * <Typography {...createCommandBarDataAttribute('productos', 'Nuevo producto', 'agregar')}>
 *   Agregar Nuevo producto
 * </Typography>
 */
export const createCommandBarDataAttribute = (section: string, title: string, action?: string) => {
  const commandBarId = generateCommandBarId(section, title, action);

  return commandBarId ? { 'data-commandbar-id': commandBarId } : {};
};
